import { hasPermission } from '@helios/authz';
import { useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/features/auth/authContext';
import { getSessionOriginKey } from '@/services/sessionOrigin';
import { accessTokenIdentity } from '@/services/authSessionClaims';
import { getAccessToken } from '@/services/authTokenStorage';

export interface SessionScopeIdentity {
  tenantId: string | undefined;
  userId: string | undefined;
  permissions: string;
  sessionOrigin: string | null;
}

export function sessionScopeKey(identity: SessionScopeIdentity) {
  return [identity.tenantId ?? 'none', identity.userId ?? 'none', identity.permissions, identity.sessionOrigin ?? 'none'] as const;
}

export function useSessionScope(permission?: string) {
  const auth = useAuth();
  const tenantId = auth.authenticated ? auth.tenant?.id ?? auth.user?.tenantId : undefined;
  const userId = auth.authenticated ? auth.user?.id : undefined;
  const permissions = JSON.stringify([...new Set(auth.permissions)].sort());
  const sessionOrigin = getSessionOriginKey();
  const claims: unknown = JSON.parse(accessTokenIdentity(getAccessToken()) ?? 'null');
  const originMatchesActor = Array.isArray(claims) && claims[0] === userId && claims[1] === tenantId && claims[4] === 'NORMAL'
    && (auth.sessionId === undefined || claims[2] === auth.sessionId)
    && (auth.authVersion === undefined || claims[3] === auth.authVersion);
  const identity = useMemo<SessionScopeIdentity>(() => ({ tenantId, userId, permissions, sessionOrigin }), [tenantId, userId, permissions, sessionOrigin]);
  const enabled = auth.authenticated && auth.accessMode !== 'PASSWORD_CHANGE_REQUIRED'
    && Boolean(identity.tenantId && identity.userId && identity.sessionOrigin) && auth.user?.tenantId === identity.tenantId && originMatchesActor
    && (!permission || hasPermission(auth.permissions, permission));
  return { identity, enabled };
}

export function isSessionScopeCurrent(origin: SessionScopeIdentity): boolean {
  return origin.sessionOrigin !== null && origin.sessionOrigin === getSessionOriginKey();
}

export function useSessionScopeFence(permission?: string) {
  const scope = useSessionScope(permission);
  const current = useRef(scope);
  current.current = scope;
  const isCurrent = useCallback((origin: SessionScopeIdentity) => current.current.enabled
    && isSessionScopeCurrent(origin)
    && sessionScopeKey(origin).every((part, index) => part === sessionScopeKey(current.current.identity)[index]), []);
  return { ...scope, isCurrent };
}
